import type { SurveyState } from './survey-selectors';
import { STARTING, SUCCESS, FAILURE } from '../action/util/action-name-creators';

import { Action, updateResourceByParam, setStateDirtyByParam } from './utils/state-manager';
import { convertArrayToMap } from '../util/convert-array-to-map';

const InitialState: SurveyState = {
  surveysById: {},
  surveysStateByOrgId: {},
  surveyResponsesById: {},
  surveyResponsesStateBySurveyId: {},
  surveyResponsesStateBySurveyResponseId: {},
};

export const surveyReducer = (state = InitialState, action: Action) => {
  switch (action.type) {
    case STARTING('LOAD_SURVEYS'): {
      const { orgId } = action.payload.args;
      return updateResourceByParam(state, 'surveys', 'orgId', orgId, true);
    }
    case SUCCESS('LOAD_SURVEYS'): {
      const {
        args: { orgId },
        data,
      } = action.payload;
      return updateResourceByParam(state, 'surveys', 'orgId', orgId, false, data);
    }
    case FAILURE('LOAD_SURVEYS'): {
      const { orgId } = action.payload.args;
      return updateResourceByParam(state, 'surveys', 'orgId', orgId, false);
    }
    case STARTING('LOAD_SURVEY_RESPONSES'): {
      const { surveyId } = action.payload.args;
      return updateResourceByParam(state, 'surveyResponses', 'surveyId', surveyId, true);
    }
    case SUCCESS('LOAD_SURVEY_RESPONSES'): {
      const {
        args: { surveyId },
        data,
      } = action.payload;
      return updateResourceByParam(
        state,
        'surveyResponses',
        'surveyId',
        surveyId,
        false,
        convertArrayToMap(data, (response: SurveyResponse) => String(response.surveyResponseId)),
      );
    }
    case SUCCESS('LOAD_SURVEY_RESPONSE'): {
      const {
        args: { surveyResponseId },
        data,
      } = action.payload;
      return updateResourceByParam(
        state,
        'surveyResponses',
        'surveyResponseId',
        surveyResponseId,
        false,
        {
          [surveyResponseId]: data,
        },
      );
    }
    case FAILURE('LOAD_SURVEY_RESPONSES'): {
      const { surveyId } = action.payload.args;
      return updateResourceByParam(state, 'surveyResponses', 'surveyId', surveyId, false);
    }
    case SUCCESS('UPDATE_SURVEY_RESPONSE'):
    case SUCCESS('SCHEDULE_SURVEY_RESPONSE_MESSAGE'): {
      const {
        args: { surveyResponseId, employeeId },
        data,
      } = action.payload;
      // add response dialog will not pass through an employeeId since this is important for anonymous responses and you cannot add an anonymous response for an employee
      if (employeeId) data.employeeId = employeeId;
      const newSurveyResponsesById = { ...state.surveyResponsesById, [surveyResponseId]: data };
      return { ...state, surveyResponsesById: newSurveyResponsesById };
    }
    case SUCCESS('CREATE_SURVEY'): {
      const {
        args: { orgId },
        data,
      } = action.payload;
      return updateResourceByParam(state, 'surveys', 'orgId', orgId, false, {
        [data.surveyId]: data,
      });
    }
    case SUCCESS('DELETE_SURVEY'): {
      const { data } = action.payload;
      // This shouldn't happen, but don't want to blow up the UI if the request fails
      if (!data || !data.surveyId) {
        return state;
      }
      const newSurveysById = { ...state.surveysById };
      const newSurveyResponsesById = convertArrayToMap(
        Object.values(state.surveyResponsesById).filter(
          (response) => response.surveyId !== data.surveyId,
        ),
        (s) => `${s.surveyResponseId}`,
      );
      delete newSurveysById[data.surveyId];
      delete newSurveyResponsesById[data.surveyId];
      return {
        ...state,
        surveysById: newSurveysById,
        surveyResponsesById: newSurveyResponsesById,
      };
    }
    case SUCCESS('UPDATE_SURVEY'): {
      const {
        data,
        args: {
          update: { employeeGroupIds, employeeIds },
        },
      } = action.payload;
      let newState = updateResourceByParam(state, 'surveys', 'orgId', data.orgId, false, {
        [data.surveyId]: data,
      });
      if (employeeGroupIds || employeeIds) {
        newState = setStateDirtyByParam(newState, 'surveyResponses', 'surveyId', data.surveyId);
      }
      return newState;
    }
    case SUCCESS('END_SURVEY'): {
      const { data } = action.payload;
      return updateResourceByParam(state, 'surveys', 'orgId', data.orgId, false, {
        [data.surveyId]: data,
      });
    }
    case SUCCESS('SEND_SURVEY_REMINDER'): {
      const { surveyId } = action.payload.args;
      return setStateDirtyByParam(state, 'surveyResponses', 'surveyId', surveyId);
    }
    case SUCCESS('UPDATE_EMPLOYEE'): {
      const {
        args: { employeeId },
      } = action.payload;
      const surveyIdsToSetDirty = Object.values(state.surveyResponsesById)
        .filter((sr: any) => sr.employeeId === employeeId)
        .map((sr: any) => sr.surveyId);
      if (surveyIdsToSetDirty.length === 0) {
        return state;
      }
      let newState;
      surveyIdsToSetDirty.forEach((surveyId) => {
        newState = setStateDirtyByParam(state, 'surveyResponses', 'surveyId', surveyId);
      });
      return newState;
    }
    case 'SET_SURVEY_RESPONSES_DIRTY': {
      const { surveyId } = action.payload;
      return setStateDirtyByParam(state, 'surveyResponses', 'surveyId', surveyId);
    }
    case SUCCESS('DELETE_EMPLOYEE'): {
      const { employeeId } = action.payload.args;
      const newSurveyResponsesById = convertArrayToMap(
        Object.values(state.surveyResponsesById).filter(
          (response) => response.employeeId !== employeeId,
        ),
        (s) => `${s.surveyResponseId}`,
      );
      return {
        ...state,
        surveyResponsesById: newSurveyResponsesById,
      };
    }
    default:
      return state;
  }
};
